import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, Calculator } from "lucide-react";

const fmt = (v, cur = "EUR") => {
  const n = parseFloat(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n.toLocaleString("de-DE", { style: "currency", currency: cur || "EUR" }) : "—";
};

const Field = ({ label, children, testid }) => (
  <div><div className="text-xs text-slate-500 uppercase tracking-wider font-mono">{label}</div><div data-testid={testid}>{children || "—"}</div></div>
);

const rawValue = (params, key) => {
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9äöüß]/g, "");
  const hit = Object.entries(params || {}).find(([k2]) => norm(k2) === norm(key));
  return hit && hit[1] !== "" ? String(hit[1]) : "";
};

export const WebsiteCalculationCard = ({ item }) => {
  const [rawOpen, setRawOpen] = useState(false);
  const k = item.kalkulation;
  const raw = item.website_raw_params && Object.entries(item.website_raw_params);
  const extra = [["Stärke", "calc-staerke"], ["Düsendurchmesser", "calc-duese"], ["Express", "calc-express"]]
    .map(([l, t]) => [l, t, rawValue(item.website_raw_params, l)]).filter(([, , v]) => v);
  if (!k && !raw?.length && !item.website_referenz) return null;
  return (
    <Card className="border-slate-200" data-testid="website-calculation-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Calculator className="w-4 h-4 text-slate-500" />
          <h2 className="text-base font-semibold">Website-Kalkulation</h2>
          {item.website_referenz && <Badge variant="outline" className="font-mono" data-testid="website-referenz">Ref {item.website_referenz}</Badge>}
          {item.website_typ && <Badge variant="outline">{item.website_typ}</Badge>}
          {item.agb_accepted === false && <Badge className="bg-amber-100 text-amber-700 border-amber-200" data-testid="website-agb-missing">AGB nicht bestätigt</Badge>}
        </div>
        {k && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <Field label="Material" testid="calc-material">{k.material}</Field>
            <Field label="Farbe" testid="calc-color">{k.color}</Field>
            <Field label="Menge" testid="calc-quantity">{k.quantity}</Field>
            <Field label="Netto" testid="calc-net">{fmt(k.net_price, k.currency)}</Field>
            <Field label="Brutto" testid="calc-gross">{fmt(k.gross_price, k.currency)}</Field>
            <Field label="MwSt." testid="calc-vat">{k.vat_rate ? `${k.vat_rate} %` : ""}</Field>
          </div>
        )}
        {extra.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            {extra.map(([l, t, v]) => <Field key={t} label={l} testid={t}>{l === "Express" && /^(ja|1|true|yes)$/i.test(v) ? <span className="font-semibold text-rose-700">Ja – Express</span> : v}</Field>)}
          </div>
        )}
        {item.nextcloud_folder && <Field label="Nextcloud-Ordner" testid="calc-nextcloud-folder"><span className="font-mono text-xs">{item.nextcloud_folder}</span></Field>}
        {raw?.length > 0 && (
          <div>
            <button type="button" onClick={() => setRawOpen(!rawOpen)} className="flex items-center gap-1 text-xs text-slate-600" data-testid="website-raw-toggle">
              {rawOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />} Rohdaten der Website ({raw.length})
            </button>
            {rawOpen && (
              <table className="w-full text-xs mt-2" data-testid="website-raw-table">
                <tbody>{raw.map(([key, v]) => (
                  <tr key={key} className="border-t border-slate-100"><td className="py-1 pr-3 font-mono text-slate-500">{key}</td><td className="py-1 break-all">{typeof v === "object" ? JSON.stringify(v) : String(v)}</td></tr>
                ))}</tbody>
              </table>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
